
import { supabase } from "@/integrations/supabase/client";
import { ContentItem } from "@/components/dashboard/content-card/types";
import { QueryClient } from "@tanstack/react-query";
import { normalizeDescription, normalizeTitle } from "@/utils/contentUtils";

/**
 * Fetches a content item by ID from Supabase
 */
export const fetchContentItem = async (
  contentId: string, 
  userId: string,
  queryClient: QueryClient
): Promise<ContentItem | null> => {
  console.log("[NAVIGATION DEBUG] Fetching content item:", contentId);
  
  // Check for cached data first
  const cachedData = queryClient.getQueryData<ContentItem>(["contentItem", contentId]);
  if (cachedData) {
    console.log("[NAVIGATION DEBUG] Using cached data for content item:", contentId);
    
    // Ensure the cached data has normalized text
    const normalizedCachedData = {
      ...cachedData,
      title: normalizeTitle(cachedData.title),
      description: normalizeDescription(cachedData.description)
    };
    
    console.log("[DESCRIPTION DEBUG] Cached description normalized:", normalizedCachedData.description);
    console.log("[COLOR DEBUG] Using cached collection color:", normalizedCachedData.collection_color);
    return normalizedCachedData;
  }
  
  // Fetch the content item from the database
  const { data, error } = await supabase
    .from('content_items')
    .select('*')
    .eq('id', contentId)
    .maybeSingle();
  
  if (error) {
    console.error("[FETCH ERROR]", error);
    throw error;
  }
  
  if (!data) {
    throw new Error('Content not found');
  }
  
  // Fetch the collection information directly with JOIN to get color in one query
  const { data: collectionData, error: collectionError } = await supabase
    .from('content_collections')
    .select('collection_id, collections:collection_id(color, name)')
    .eq('content_id', contentId)
    .maybeSingle();
  
  if (collectionError) {
    console.error("[COLLECTION FETCH ERROR]", collectionError);
  }
  
  let collectionColor = "#6B7789"; // Default color
  let collectionId = null;
  let collectionName = null;
  
  // Extract collection information if available
  if (collectionData?.collection_id) {
    collectionId = collectionData.collection_id;
    
    if (collectionData.collections) {
      // @ts-ignore - We know collections exists from the query
      collectionColor = collectionData.collections.color || "#6B7789";
      // @ts-ignore - We know collections exists from the query
      collectionName = collectionData.collections.name;
    } else {
      // Fallback to separate query if needed
      const { data: collection, error: collectionInfoError } = await supabase
        .from('collections')
        .select('color, name')
        .eq('id', collectionData.collection_id)
        .maybeSingle();
      
      if (collectionInfoError) {
        console.error("[COLLECTION INFO FETCH ERROR]", collectionInfoError);
      }
      
      if (collection) {
        collectionColor = collection.color || "#6B7789";
        collectionName = collection.name;
      }
    }
  }
  
  // Normalize the title and description - apply consistently to ensure all text has the same styling
  const title = normalizeTitle(data.title);
  const description = normalizeDescription(data.description);
  
  console.log("[TEXT DEBUG] Normalized title:", title);
  console.log("[TEXT DEBUG] Normalized description:", description);
  console.log("[COLOR DEBUG] Fetched content item with collection color:", {
    contentId: contentId,
    collectionId: collectionId,
    collectionColor: collectionColor,
    collectionName: collectionName,
    title: title
  });
  
  return { 
    ...data, 
    title, // Ensure title is normalized
    description, // Ensure description is normalized
    collection_id: collectionId,
    collection_color: collectionColor,
    collection_name: collectionName 
  } as ContentItem;
};
